import { isPlatformServer } from '@angular/common';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { merge } from 'rxjs/internal/observable/merge';
import { filter } from 'rxjs/internal/operators/filter';
import { throttleTime } from 'rxjs/internal/operators/throttleTime';
import { IdleInterruptArgs } from '../interrupt-args';
import { IdleInterruptOptions } from '../interrupt-options';
import { IdleInterruptSource } from '../interrupt-source';

export interface HasEventTargetAddRemove<E> {
    addEventListener(type: string, listener: ((evt: E) => void) | null, options?: boolean | AddEventListenerOptions): void;
    removeEventListener(type: string, listener?: ((evt: E) => void) | null, options?: EventListenerOptions | boolean): void;
}

export declare type EventTargetLike<T> = HasEventTargetAddRemove<T>;

export declare type EventTarget<T> = EventTargetLike<T> | ArrayLike<EventTargetLike<T>>;

/**
 * Options for InterruptSourceEventTarget
 */
export interface IdleInterruptSourceEventTargetOptions {
    /**
     * The number of milliseconds to throttle the events coming from the target.
     */
    throttleDelay?: number;

    /**
     * Whether or not to use passive event listeners.
     * Note: you need to detect if the browser supports passive listeners, and only set this to true if it does.
     */
    passive?: boolean;
}

const defaultThrottleDelay = 500;

/**
 * An interrupt source on an EventTarget object, such as a Window or HTMLElement.
 */
export class IdleInterruptSourceEventTarget extends IdleInterruptSource {
    private eventSrc: Observable<any> | null = null;
    private eventSubscription: Subscription = new Subscription();
    protected throttleDelay: number;
    protected passive: boolean;

    constructor(
        protected target: EventTarget<any> | (() => EventTarget<any>),
        protected events: string,
        private opts?: number | IdleInterruptSourceEventTargetOptions
    ) {
        super(undefined, undefined);

        if (typeof this.opts === 'number') {
            this.opts = { throttleDelay: this.opts, passive: false };
        }

        this.opts = this.opts || {
            passive: false,
            throttleDelay: defaultThrottleDelay
        };

        if (this.opts.throttleDelay === undefined || this.opts.throttleDelay === null) {
            this.opts.throttleDelay = defaultThrottleDelay;
        }

        this.throttleDelay = this.opts.throttleDelay;
        this.passive = !!this.opts.passive;
    }

    override initialize(options?: IdleInterruptOptions) {
        if (options?.platformId && isPlatformServer(options.platformId)) {
            return;
        }

        const eventTarget = typeof this.target === 'function' ? this.target() : this.target;
        const opts = this.passive ? { passive: true } : null;
        const fromEvents = this.events.split(' ').map(eventName => {
            return fromEvent(eventTarget, eventName, opts);
        });

        this.eventSrc = merge(...fromEvents);
        this.eventSrc = this.eventSrc.pipe(filter(innerArgs => !this.filterEvent(innerArgs)));

        if (this.throttleDelay > 0) {
            this.eventSrc = this.eventSrc.pipe(throttleTime(this.throttleDelay));
        }

        const handler = (innerArgs: any) => {
            this.onInterrupt.emit(new IdleInterruptArgs(this, innerArgs));
        };

        this.attachFn = () => (this.eventSubscription = this.eventSrc!.subscribe(handler));

        this.detachFn = () => this.eventSubscription?.unsubscribe();
    }

    /**
     * Checks to see if the event should be filtered. Always returns false unless overridden.
     * @param event - The original event object.
     * @return True if the event should be filtered (don't cause an interrupt); otherwise, false.
     */
    protected filterEvent(event: any): boolean {
        return false;
    }

    /**
     * Returns the current options being used.
     * @return The current option values.
     */
    get options(): IdleInterruptSourceEventTargetOptions {
        return {
            passive: this.passive,
            throttleDelay: this.throttleDelay
        };
    }
}
